import React from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  PanTool
} from '@mui/icons-material';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { ChartsTooltipContainer, ChartsTooltipPaper, useItemTooltip } from '@mui/x-charts/ChartsTooltip';

function MapComponent({ accords, onAccordClick }) {
  const theme = useTheme();
  
  // State for zoom functionality - must be at the top before any returns
  const [zoomState, setZoomState] = React.useState({
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null,
  });

  // State for panning/dragging
  const [isPanning, setIsPanning] = React.useState(false);
  const [panStart, setPanStart] = React.useState({ x: 0, y: 0 });
  const [panMode, setPanMode] = React.useState(false); // Toggle between pan and click mode
  const lastPanUpdateRef = React.useRef(0); // For throttling pan updates

  // State for stable jitter - recalculated only when accords change
  const [jitterMap, setJitterMap] = React.useState(new Map());

  // Calculate axis domains to ensure all points are visible with better scaling
  const allXValues = accords.map(a => a.x).filter(x => x !== null && x !== undefined && !isNaN(x));
  const allYValues = accords.map(a => a.y).filter(y => y !== null && y !== undefined && !isNaN(y));
  
  // Calculate coordinate statistics for later use
  const xMin = allXValues.length > 0 ? Math.min(...allXValues) : 0;
  const xMax = allXValues.length > 0 ? Math.max(...allXValues) : 1;
  const yMin = allYValues.length > 0 ? Math.min(...allYValues) : 0;
  const yMax = allYValues.length > 0 ? Math.max(...allYValues) : 1;
  
  // Calculate padding
  const isVerySmallRange = (xMax - xMin) < 1 && (yMax - yMin) < 1;
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xPadding = Math.max(xRange * 0.2, 0.5);
  const yPadding = Math.max(yRange * 0.2, 0.5);

  // Generate stable jitter values when accords change
  React.useEffect(() => {
    const newJitterMap = new Map();
    const jitterAmountX = Math.max(xRange, 0.1) * 0.05; // 5% of range for better separation
    const jitterAmountY = Math.max(yRange, 0.1) * 0.05;
    
    accords.forEach((accord) => {
      // Use accord ID as seed for deterministic jitter
      const seed = accord.id ? accord.id.toString() : Math.random().toString();
      
      // Simple hash function to convert ID to pseudo-random values
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      // Use hash to generate consistent jitter values between -0.5 and 0.5
      const jitterX = ((hash % 1000) / 1000 - 0.5) * jitterAmountX;
      const jitterY = (((hash * 31) % 1000) / 1000 - 0.5) * jitterAmountY;
      
      newJitterMap.set(accord.id, { x: jitterX, y: jitterY });
    });
    
    setJitterMap(newJitterMap);
  }, [accords, xRange, yRange]); // Recalculate when accords or ranges change

  // Handle zoom functionality - must be defined before early return
  const handleWheel = React.useCallback((event) => {
    event.preventDefault();
    
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Calculate relative position (0-1) within the chart area
    const chartLeft = 60; // left margin
    const chartRight = 1000 - 320; // width - right margin  
    const chartTop = 60; // top margin
    const chartBottom = 700 - 60; // height - bottom margin
    
    const relativeX = (x - chartLeft) / (chartRight - chartLeft);
    const relativeY = (y - chartTop) / (chartBottom - chartTop);
    
    // Only zoom if cursor is within chart bounds
    if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
      return;
    }
    
    const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9; // zoom out or in
    
    const currentXMin = zoomState.xMin !== null ? zoomState.xMin : xMin - xPadding;
    const currentXMax = zoomState.xMax !== null ? zoomState.xMax : xMax + xPadding;
    const currentYMin = zoomState.yMin !== null ? zoomState.yMin : yMin - yPadding;
    const currentYMax = zoomState.yMax !== null ? zoomState.yMax : yMax + yPadding;
    
    const currentXRange = currentXMax - currentXMin;
    const currentYRange = currentYMax - currentYMin;
    
    // Calculate the point in data coordinates where the mouse is
    const mouseDataX = currentXMin + relativeX * currentXRange;
    const mouseDataY = currentYMax - relativeY * currentYRange; // Invert Y because SVG coords are flipped
    
    // Center the zoom around the mouse position
    const newXMin = mouseDataX - (mouseDataX - currentXMin) * zoomFactor;
    const newXMax = mouseDataX + (currentXMax - mouseDataX) * zoomFactor;
    const newYMin = mouseDataY - (mouseDataY - currentYMin) * zoomFactor;
    const newYMax = mouseDataY + (currentYMax - mouseDataY) * zoomFactor;
    
    setZoomState({
      xMin: newXMin,
      xMax: newXMax,
      yMin: newYMin,
      yMax: newYMax,
    });
  }, [zoomState, xMin, xMax, yMin, yMax, xPadding, yPadding]);

  // Reset zoom function
  const resetZoom = React.useCallback(() => {
    setZoomState({
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null,
    });
  }, []);

  // Manual zoom functions
  const zoomIn = React.useCallback(() => {
    const currentXMin = zoomState.xMin !== null ? zoomState.xMin : xMin - xPadding;
    const currentXMax = zoomState.xMax !== null ? zoomState.xMax : xMax + xPadding;
    const currentYMin = zoomState.yMin !== null ? zoomState.yMin : yMin - yPadding;
    const currentYMax = zoomState.yMax !== null ? zoomState.yMax : yMax + yPadding;
    
    const centerX = (currentXMin + currentXMax) / 2;
    const centerY = (currentYMin + currentYMax) / 2;
    const zoomFactor = 0.8; // Zoom in factor
    
    const newXRange = (currentXMax - currentXMin) * zoomFactor;
    const newYRange = (currentYMax - currentYMin) * zoomFactor;
    
    setZoomState({
      xMin: centerX - newXRange / 2,
      xMax: centerX + newXRange / 2,
      yMin: centerY - newYRange / 2,
      yMax: centerY + newYRange / 2,
    });
  }, [zoomState, xMin, xMax, yMin, yMax, xPadding, yPadding]);

  const zoomOut = React.useCallback(() => {
    const currentXMin = zoomState.xMin !== null ? zoomState.xMin : xMin - xPadding;
    const currentXMax = zoomState.xMax !== null ? zoomState.xMax : xMax + xPadding;
    const currentYMin = zoomState.yMin !== null ? zoomState.yMin : yMin - yPadding;
    const currentYMax = zoomState.yMax !== null ? zoomState.yMax : yMax + yPadding;
    
    const centerX = (currentXMin + currentXMax) / 2;
    const centerY = (currentYMin + currentYMax) / 2;
    const zoomFactor = 1.25; // Zoom out factor
    
    const newXRange = (currentXMax - currentXMin) * zoomFactor;
    const newYRange = (currentYMax - currentYMin) * zoomFactor;
    
    setZoomState({
      xMin: centerX - newXRange / 2,
      xMax: centerX + newXRange / 2,
      yMin: centerY - newYRange / 2,
      yMax: centerY + newYRange / 2,
    });
  }, [zoomState, xMin, xMax, yMin, yMax, xPadding, yPadding]);

  // Pan functions
  const handleMouseDown = React.useCallback((event) => {
    if (panMode) {
      setIsPanning(true);
      setPanStart({ x: event.clientX, y: event.clientY });
      event.preventDefault();
      event.stopPropagation();
      // Ensure we capture the mouse for consistent panning
      if (event.target && event.target.setCapture) {
        event.target.setCapture();
      }
    }
  }, [panMode]);

  const handleMouseMove = React.useCallback((event) => {
    if (isPanning && panMode) {
      // Prevent default behavior during panning
      event.preventDefault();
      event.stopPropagation();
      
      // Throttle updates to prevent too many rapid renders (max 60fps)
      const now = Date.now();
      if (now - lastPanUpdateRef.current < 16) {
        return;
      }
      lastPanUpdateRef.current = now;
      
      const deltaX = event.clientX - panStart.x;
      const deltaY = event.clientY - panStart.y;
      
      // Only update if there's significant movement to avoid micro-movements
      if (Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) {
        return;
      }
      
      const currentXMin = zoomState.xMin !== null ? zoomState.xMin : xMin - xPadding;
      const currentXMax = zoomState.xMax !== null ? zoomState.xMax : xMax + xPadding;
      const currentYMin = zoomState.yMin !== null ? zoomState.yMin : yMin - yPadding;
      const currentYMax = zoomState.yMax !== null ? zoomState.yMax : yMax + yPadding;
      
      // Convert pixel movement to data coordinates
      const chartWidth = 1000 - 60 - 320; // width - left margin - right margin
      const chartHeight = 700 - 60 - 60; // height - top margin - bottom margin
      
      const xDataDelta = -(deltaX / chartWidth) * (currentXMax - currentXMin);
      const yDataDelta = (deltaY / chartHeight) * (currentYMax - currentYMin);
      
      setZoomState({
        xMin: currentXMin + xDataDelta,
        xMax: currentXMax + xDataDelta,
        yMin: currentYMin + yDataDelta,
        yMax: currentYMax + yDataDelta,
      });
      
      setPanStart({ x: event.clientX, y: event.clientY });
    }
  }, [isPanning, panMode, panStart, zoomState, xMin, xMax, yMin, yMax, xPadding, yPadding]);

  const handleMouseUp = React.useCallback((event) => {
    if (isPanning) {
      setIsPanning(false);
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        // Release mouse capture if it was set
        if (event.target && event.target.releaseCapture) {
          event.target.releaseCapture();
        }
      }
    }
  }, [isPanning]);

  // Add event listeners for mouse move and up on document
  React.useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp]);

  // Early return after all hooks are defined
  if (allXValues.length === 0 || allYValues.length === 0) {
    console.warn('No valid coordinate data found');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography>Dados de coordenadas não disponíveis</Typography>
      </Box>
    );
  }

  // Calculate coordinate duplicates
  const coordPairs = allXValues.map((x, i) => `${x},${allYValues[i]}`);
  const uniqueCoords = new Set(coordPairs);
  const duplicateCount = allXValues.length - uniqueCoords.size;
  
  // Log the most common coordinate pairs to understand clustering
  const coordCounts = {};
  coordPairs.forEach(coord => {
    coordCounts[coord] = (coordCounts[coord] || 0) + 1;
  });
  const sortedCoords = Object.entries(coordCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  
  console.log('Coordinate ranges:', { 
    xMin, xMax, yMin, yMax, 
    xRange: xMax - xMin, 
    yRange: yMax - yMin,
    totalPoints: allXValues.length,
    uniqueCoordinates: uniqueCoords.size,
    duplicatePoints: duplicateCount,
    topDuplicates: sortedCoords,
    sampleValues: {
      x: allXValues.slice(0, 5),
      y: allYValues.slice(0, 5)
    }
  });
  
  console.log('Is coordinate range very small?', isVerySmallRange);
  
  const xDomain = [
    zoomState.xMin !== null ? zoomState.xMin : xMin - xPadding, 
    zoomState.xMax !== null ? zoomState.xMax : xMax + xPadding
  ];
  const yDomain = [
    zoomState.yMin !== null ? zoomState.yMin : yMin - yPadding, 
    zoomState.yMax !== null ? zoomState.yMax : yMax + yPadding
  ];

  const legendItems = [
    { name: "Provido", color: "#337357", count: accords.filter(a => a.result === "Provido").length },
    { name: "Não provido", color: "#E50046", count: accords.filter(a => a.result === "Não provido").length },
    { name: "Parcialmente provido", color: "#FFD23F", count: accords.filter(a => a.result === "Parcialmente provido").length },
    { name: "Não conhecido", color: "#0288d1", count: accords.filter(a => a.result === "Não conhecido").length },
    { name: "Extinto", color: "#000000", count: accords.filter(a => a.result === "Extinto").length },
    { name: "Carregado pelo usuário", color: "#5E1675", count: accords.filter(a => a.result === "Carregado pelo usuário").length },
    { name: "Desconhecido", color: "#757575", count: accords.filter(a => !["Provido", "Não provido", "Parcialmente provido", "Não conhecido", "Mantido", "Rejeitado"].includes(a.result)).length },
  ].filter(item => item.count > 0);

  // Prepare data for scatter chart grouped by result type
  const scatterDataBase = legendItems.map((item, index) => {
    const itemAccords = accords.filter(accord => {
      // Exclude the uploaded case from the base clusters; it will be rendered in its own series
      if (accord.uploadedCase) return false;
      if (item.name === "Desconhecido") {
        return !["Provido", "Não provido", "Parcialmente provido", "Não conhecido", "Mantido", "Rejeitado"].includes(accord.result);
      }
      return accord.result === item.name;
    });
    
    return {
      id: `series-${index}`, // Add explicit ID for series lookup
      label: item.name,
      data: itemAccords.map((accord, accordIndex) => {
        // Use stable jitter values from the map
        const jitter = jitterMap.get(accord.id) || { x: 0, y: 0 };
        
        return {
          x: accord.x + jitter.x, 
          y: accord.y + jitter.y, 
          id: accord.id,
          originalX: accord.x, // Keep original coordinates for reference
          originalY: accord.y,
          uploadedCase: !!accord.uploadedCase,
        };
      }),
      color: item.color,
      markerSize: 3, // Make markers larger for easier clicking
    };
  }).filter(series => series.data.length > 0);

  // If there is any accord flagged as uploadedCase, add a dedicated series with triangle markers
  const uploadedCasePoints = accords.filter(a => a.uploadedCase);
  const uploadedSeries = uploadedCasePoints.length > 0 ? [{
    id: 'uploaded-case-series',
    label: 'Caso carregado',
    data: uploadedCasePoints.map((accord) => {
      const jitter = jitterMap.get(accord.id) || { x: 0, y: 0 };
      return {
        x: accord.x + jitter.x,
        y: accord.y + jitter.y,
        id: accord.id,
        originalX: accord.x,
        originalY: accord.y,
        uploadedCase: true,
      };
    }),
    color: '#5E1675', // purple to match legend and be unique
    markerSize: 6,
    // Use triangle shape if supported by library version via markerShape
    markerShape: 'triangle',
  }] : [];

  const scatterData = [...scatterDataBase, ...uploadedSeries];

  // Helpers for tooltip formatting
  const stripTags = (text) => {
    if (!text) return '';
    return String(text).replace(/<[^>]*>/g, '');
  };
  const truncate = (text, maxLength = 150) => {
    if (!text) return 'Não informado';
    const clean = stripTags(text);
    return clean.length > maxLength ? clean.substring(0, maxLength) + '...' : clean;
  };

  // Custom tooltip component to render multi-line content with bold labels
  function CustomItemTooltip(props) {
    const tip = useItemTooltip();
    if (!tip) return null;

    const seriesLabel = tip.label; // our series label matches legend item name
    const dataIndex = tip.identifier?.dataIndex ?? 0;

    const series = scatterData.find(s => s.label === seriesLabel);
    const datum = series?.data?.[dataIndex];
    const accord = datum ? accords.find(a => String(a.id) === String(datum.id)) : undefined;

    const ano = accord?.ano ?? 'Não informado';
    const sessao = accord?.sessao ?? 'Não informado';
    const processo = truncate(accord?.processo);
    const resultado = truncate(accord?.result);
    const ementa = truncate(accord?.ementa);
    const penalidade = truncate(accord?.penalidade);
    const baseLegal = truncate(accord?.baseLegal);

    return (
      <ChartsTooltipContainer {...props} trigger="item">
        <ChartsTooltipPaper sx={{ p: 1.25, maxWidth: 420 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, whiteSpace: 'normal' }}>
            <Typography variant="body2"><strong>Ano:</strong> {ano}</Typography>
            <Typography variant="body2"><strong>Sessão:</strong> {sessao}</Typography>
            <Typography variant="body2"><strong>Processo:</strong> {processo}</Typography>
            <Typography variant="body2"><strong>Resultado:</strong> {resultado}</Typography>
            <Typography variant="body2"><strong>Ementa:</strong> {ementa}</Typography>
            <Typography variant="body2"><strong>Penalidade:</strong> {penalidade}</Typography>
            <Typography variant="body2"><strong>Base Legal:</strong> {baseLegal}</Typography>
          </Box>
        </ChartsTooltipPaper>
      </ChartsTooltipContainer>
    );
  }

  // Handle scatter chart item clicks to add accords to the grid
  const handleItemClick = (event, itemIdentifier) => {
    console.log('Scatter chart item clicked:', { event, itemIdentifier });
    console.log('Pan mode status:', panMode);
    console.log('Available series data:', scatterData);
    
    if (!itemIdentifier || !itemIdentifier.seriesId || itemIdentifier.dataIndex === undefined) {
      console.error('Invalid item identifier:', itemIdentifier);
      return;
    }

    const { seriesId, dataIndex } = itemIdentifier;
    console.log('Looking for series ID:', seriesId, 'dataIndex:', dataIndex);
    
    // Find the series by ID
  const series = scatterData.find(s => s.id === seriesId);
    console.log('Found series:', series, 'with', series?.data?.length, 'data points');
    
    if (series && series.data && series.data[dataIndex]) {
      const dataPoint = series.data[dataIndex];
      console.log('Found data point:', dataPoint);
      
      // Ignore clicks on the uploaded case point for selection
      if (dataPoint.uploadedCase) {
        console.log('Uploaded case point clicked; ignoring selection.');
        return;
      }

      // Find the accord by ID
      const accord = accords.find(a => String(a.id) === String(dataPoint.id));
      console.log('Found accord:', accord);
      
      if (accord && onAccordClick) {
        console.log('Calling onAccordClick with accord:', accord.id);
        onAccordClick(accord);
      } else {
        console.error('Accord not found or onAccordClick not available');
      }
    } else {
      console.error('Series or data point not found:', {
        seriesExists: !!series,
        dataExists: !!(series?.data),
        dataIndex,
        dataLength: series?.data?.length,
        allSeriesIds: scatterData.map(s => s.id)
      });
    }
  };  return (
    <Box sx={{ position: 'relative', width: '100%', height: '700px' }}>
      {/* Map Container */}
      <Box 
        sx={{ 
          width: '100%', 
          height: '100%', 
          border: `2px solid ${alpha(theme.palette.primary.main, panMode ? 0.3 : 0.1)}`,
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          background: panMode 
            ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          cursor: panMode ? (isPanning ? 'grabbing' : 'grab') : 'pointer',
          transition: 'border-color 0.2s ease, background 0.2s ease',
        }}
        onWheel={handleWheel}
        onMouseDown={panMode ? handleMouseDown : undefined}
        onMouseMove={panMode && isPanning ? handleMouseMove : undefined}
      >
        <ScatterChart
          width={1000}
          height={700}
          series={scatterData}
          margin={{ left: 60, right: 320, top: 20, bottom: 60 }} // Reduced top margin since no legend
          grid={{ horizontal: true, vertical: true }}
          disableLegend={true}
          legend={{ hidden: true }} // Additional legend hiding
          sx={{
            '& .MuiChartsLegend-root': {
              display: 'none !important'
            },
            // Disable all pointer events when in pan mode
            pointerEvents: panMode ? 'none' : 'auto',
            '& *': {
              pointerEvents: panMode ? 'none !important' : 'auto',
            }
          }}
          onItemClick={!panMode ? (event, params) => {
            console.log('ScatterChart onItemClick triggered:', { event, params });
            handleItemClick(event, params);
          } : () => {}}
          onClick={!panMode ? (event) => {
            console.log('ScatterChart onClick (fallback) triggered:', event);
          } : () => {}}
          xAxis={[{
            min: xDomain[0],
            max: xDomain[1],
            type: 'linear',
            hideTooltip: true,
            tickLabelStyle: { display: 'none' },
            axisLine: { stroke: theme.palette.divider },
            tickLine: { display: 'none' },
          }]}
          yAxis={[{
            min: yDomain[0],
            max: yDomain[1],
            type: 'linear',
            hideTooltip: true,
            tickLabelStyle: { display: 'none' },
            axisLine: { stroke: theme.palette.divider },
            tickLine: { display: 'none' },
          }]}
          slots={{ tooltip: CustomItemTooltip }}
          slotProps={{
            tooltip: { trigger: 'item' },
          }}
        />        {/* Zoom Controls */}
        <Box 
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            borderRadius: 2,
            p: 1,
            boxShadow: 2,
            zIndex: 1001, // Ensure controls are above the overlay
          }}
        >
          <Tooltip title="Aumentar Zoom" placement="right">
            <IconButton 
              onClick={zoomIn}
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <ZoomIn />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Diminuir Zoom" placement="right">
            <IconButton 
              onClick={zoomOut}
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <ZoomOut />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={panMode ? "Modo Seleção" : "Modo Mover"} placement="right">
            <IconButton 
              onClick={() => setPanMode(!panMode)}
              size="small"
              sx={{ 
                backgroundColor: panMode ? alpha(theme.palette.secondary.main, 0.2) : alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: panMode ? alpha(theme.palette.secondary.main, 0.3) : alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <PanTool />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Resetar Zoom" placement="right">
            <IconButton 
              onClick={resetZoom}
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <CenterFocusStrong />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Legend */}
      <Paper 
        elevation={3}
        sx={{ 
          position: 'absolute', 
          top: 16, 
          right: 16, 
          p: 2, 
          maxWidth: 280,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          zIndex: 1001, // Ensure it's above the overlay and chart
        }}
      >
        <Typography 
          variant="subtitle1" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Resultados
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Use a roda do mouse para zoom ou os botões de controle no canto superior esquerdo. Ative o modo "Mover" para arrastar o gráfico.
        </Typography>
        <List dense sx={{ p: 0 }}>
          {legendItems.map((item) => (
            <ListItem key={item.name} disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <Box 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    bgcolor: item.color,
                    border: '2px solid white',
                    boxShadow: 1,
                  }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {item.name}
                    </Typography>
                    <Chip 
                      label={item.count}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        height: 20,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}

export default MapComponent;
